import { useState, useCallback } from 'react';
import { API_URL } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

export const useDeployments = () => {
  const { getAuthHeaders } = useAuth();
  const [products, setProducts] = useState([]);
  const [deploymentStats, setDeploymentStats] = useState({
    total: 0,
    successful: 0,
    failed: 0,
    inProgress: 0,
    totalChange: 0,
    successChange: 0,
    failChange: 0
  });

  const fetchDeployments = useCallback(async () => {
    try {
      const productsResponse = await fetch(`${API_URL}/products`, {
        headers: getAuthHeaders()
      });
      
      if (!productsResponse.ok) {
        console.error('Authentication failed:', productsResponse.status);
        setProducts([]);
        return;
      }
      
      const productsData = await productsResponse.json();

      const buildsResponse = await fetch(`${API_URL}/builds`, {
        headers: getAuthHeaders()
      });
      
      if (!buildsResponse.ok) {
        console.error('Authentication failed:', buildsResponse.status);
        setProducts([]);
        return;
      }
      
      const buildsData = await buildsResponse.json();
      
      if (!Array.isArray(productsData) || !Array.isArray(buildsData)) {
        console.error('Invalid data format');
        setProducts([]);
        return;
      }
      
      const buildsByProduct = buildsData.reduce((acc, build) => {
        if (!acc[build.product_id]) {
          acc[build.product_id] = [];
        }
        
        acc[build.product_id].push({
          id: build.id,
          tag: build.tag,
          branch: build.branch_name,
          time: build.date_time,
          status: build.status,
          environment: build.environment,
          deployment_type: build.deployment_type,
          approved_by: build.approved_by,
          details: {
            date_time: build.date_time,
            author_name: build.author_name,
            build_number: build.build_number,
            commit_id: build.commit_id,
            commit_message: build.commit_message,
            branch_name: build.branch_name,
            author_email: build.author_email,
            domain_name: build.domain_name,
            image_name: build.image_name
          }
        });
        
        return acc;
      }, {});
      
      const productsArray = productsData.map(product => {
        const productBuilds = buildsByProduct[product.id] || [];
        const successCount = productBuilds.filter(d => d.status === 'Success').length;
        const totalCount = productBuilds.length;
        
        return {
          id: product.id,
          name: product.name,
          description: product.description,
          color: product.color || 'blue',
          version_url: product.version_url,
          group_id: product.group_id,
          deployments: productBuilds,
          dockerTags: productBuilds.length,
          successRate: totalCount > 0 ? Math.round((successCount / totalCount) * 100) : 0,
          totalDeployments: totalCount
        };
      });
      
      setProducts(productsArray);
    } catch (error) {
      console.error('Error fetching deployments:', error);
      setProducts([]);
    }
  }, [getAuthHeaders]);

  const fetchDeploymentStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/builds/stats/summary`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      
      setDeploymentStats({
        total: parseInt(data.total) || 0,
        successful: parseInt(data.successful) || 0,
        failed: parseInt(data.failed) || 0,
        inProgress: parseInt(data.in_progress) || 0,
        totalChange: 12,
        successChange: 8,
        failChange: -5
      });
    } catch (error) {
      console.error('Error fetching deployment stats:', error);
    }
  }, [getAuthHeaders]);

  return {
    products,
    setProducts,
    deploymentStats,
    fetchDeployments,
    fetchDeploymentStats
  };
};
